'use client'
import { Link } from '@/i18n/routing'
import { IconArrowRight } from '@tabler/icons-react'
import classNames from 'classnames'
import { useTranslations } from 'next-intl'
import { FC } from 'react'
import { isLinkActive, type MenuColumn } from '../menu'

type MegaMenuPanelProps = {
  id: string
  menuId: string
  columns: MenuColumn[]
  showContact?: boolean
  hidden: boolean
  pathname: string
}

const columnTitleClassName = 'mb-4 text-xs uppercase tracking-[0.2em] text-KMRSx24-GreyLight'
const linkClassName =
  'group inline-flex items-center gap-x-2 py-1 text-base xl:text-lg font-light leading-6 transition-colors duration-150 hover:text-accent focus-visible:text-accent focus-visible:outline-none'

// Ein Panel je Hauptpunkt. Alle Panels stehen von Anfang an im HTML (hidden),
// damit die Links auch ohne Hover im ausgelieferten Markup und damit fuer
// Crawler sichtbar sind. Die Einblendung startet bei jedem Wechsel von hidden
// auf sichtbar neu und braucht deshalb kein JS.
const MegaMenuPanel: FC<MegaMenuPanelProps> = ({ id, menuId, columns, showContact = false, hidden, pathname }) => {
  const t = useTranslations('navigation')

  return (
    <div id={id} hidden={hidden} data-nav-menu={menuId} className="pt-8 motion-safe:animate-[nav-panel-in_260ms_ease-out]">
      <div className="grid grid-cols-4 gap-x-8 border-t border-white/10 pt-8">
        {columns.map((column) => (
          <div key={column.title}>
            <p className={columnTitleClassName}>{t(column.title)}</p>
            <ul className="flex flex-col gap-y-2">
              {column.links.map((link) => {
                const isActive = isLinkActive(pathname, link)
                return (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      data-nav-link={link.href}
                      aria-current={isActive ? 'page' : undefined}
                      className={classNames(linkClassName, link.highlight || isActive ? 'text-accent' : 'text-white')}
                    >
                      {t(link.label)}
                      {link.highlight && <IconArrowRight size={18} aria-hidden="true" className="transition-transform duration-150 group-hover:translate-x-1" />}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
        {showContact && (
          <div>
            <p className={columnTitleClassName}>{t('menu.directContact')}</p>
            <ul className="flex flex-col gap-y-2">
              <li>
                <a href="tel:+4991147734580" data-nav-link="tel" className={classNames(linkClassName, 'text-white')}>
                  <span className="sr-only">{t('menu.phone')}: </span>+49 (911) 477 345 80
                </a>
              </li>
              <li>
                <a href="mailto:hello@kommers.io" data-nav-link="mailto" className={classNames(linkClassName, 'text-white')}>
                  <span className="sr-only">{t('menu.email')}: </span>hello@kommers.io
                </a>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}

export default MegaMenuPanel
